import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { ApiResult, client, wrapErrorWithResult } from '../../api';
import { useAddress, useSessionCustomer } from '../../customer';
import { RedirectToLogin } from '../../router';
import { interpolate } from '../../cms';
import { usePendingOrder, useSubscription } from '../../subscriptions';
import { useToast, TOAST_CLIENT_ERROR_MESSAGE } from '../../Toast';
import { useProduct } from '../../products';
import { useCurrentPet } from '../useCurrentPet';
import { WelcomeBack } from '../WelcomeBack';
import { DogSelection } from '../DogSelection';
import { YourPup } from '../YourPup';
import { NextBox } from '../NextBox';
import { VetServicesModal } from '../VetServicesModal';
import { BoxManagementNav } from '../BoxManagementNav';
import { VetServices } from '../VetServices';
import { AccountHeader } from '../AccountHeader';
import { useReporter } from '../../reporter';
import { Footer, LoadingView, LearnMore, BlogLinks } from '../../components';
import { ReferAFriend } from '../ReferAFriend';
import { FOOTER_CONTENT } from '../../testing/constants';
import { usePayment } from '../../payment';
import { usePets, useLatestOrderForPetId } from '../../pets';
import {
  addDog,
  openVetServicesModalEvent,
  viewDashboardPageEvent,
} from './events';
import {
  getDogs,
  getYourPupProps,
  getNextBoxProps,
  getBoxManagementNavProps,
} from './dataParsers';
import {
  StyledAccountContainer,
  HeaderContainer,
  ContentContainer,
  YourPupContainer,
  SectionContainer,
} from './styles';
// TODO remove constants
import {
  VET_SERVICES,
  VET_SERVICES_ERROR,
  VET_SERVICES_MODAL,
  VET_SERVICES_URL,
  ACTIVE_STATUS,
} from './constants';

export const AccountContainer = ({
  headline,
  subheadline,
  dogSelectorLabel,
  addPupBtnLabel,
  addPupBtnUrl,
  links,
  logoUrl,
  logoSrText,
  yourPup,
  discountAmount,
  learnMoreBtnLabel,
  learnMoreHeading,
  learnMoreBody,
  learnMoreHref,
  learnMoreImg,
  blogs,
  blogHeading,
  blogFontColor,
}) => {
  const [vetServicesModalShown, setVetServicesModalShown] = useState(false);
  const reporter = useReporter();
  const router = useRouter();
  const { showToast } = useToast();
  const { pets, isLoading: arePetsLoading } = usePets({
    hasSubscription: true,
  });
  const { pet, isLoading: isPetLoading } = useCurrentPet();
  const petOptions = useMemo(() => getDogs(pets), [pets]);
  const selectedPetId = pet?.id;

  const {
    subscription,
    nextDeliveryDate,
    isLoading: isSubscriptionLoading,
  } = useSubscription(selectedPetId);

  const {
    customer,
    isActive,
    isLoading: isCustomerLoading,
  } = useSessionCustomer();

  const { product, isLoading: isProductLoading } = useProduct(
    subscription?.sku,
  );

  const {
    order: latestOrder,
    isLoading: isLatestOrderLoading,
  } = useLatestOrderForPetId(pet?.id);
  const {
    order: pendingOrder,
    isLoading: isPendingOrderLoading,
  } = usePendingOrder(subscription?.id);

  const { payment, isLoading: isPaymentLoading } = usePayment(customer?.id);

  const { address, isLoading: isAddressLoading } = useAddress(customer?.id);

  const onVetCtaClick = async () => {
    try {
      const { data } = await client.get(VET_SERVICES_URL);
      router.push(data.url);
    } catch (err) {
      const result = wrapErrorWithResult(err);
      ApiResult.match(result, {
        error: {
          client: error => {
            reporter.error(error.original);
            showToast({
              status: 'error',
              message:
                error.original.response?.data?.detail ??
                TOAST_CLIENT_ERROR_MESSAGE,
              headline: 'Error Message',
            });
          },
          server: () => {
            showToast({
              status: 'error',
              message: VET_SERVICES_ERROR,
              headline: 'Error Message',
            });
          },
        },
      });
    }
  };

  const onSelectPet = id => {
    reporter.tag(addDog('dropdown'));
    // URL is duplicated because second param is deprecated `as`
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, pet_id: id },
      },
      {
        pathname: router.pathname,
        query: { ...router.query, pet_id: id },
      },
      { shallow: true },
    );
  };
  const isLoading =
    isPetLoading ||
    isSubscriptionLoading ||
    isCustomerLoading ||
    isProductLoading ||
    isPaymentLoading ||
    isAddressLoading ||
    arePetsLoading ||
    isLatestOrderLoading ||
    isPendingOrderLoading;

  useEffect(() => {
    if (!isLoading) reporter.tag(viewDashboardPageEvent());
  }, [reporter, isLoading]);

  if (!isCustomerLoading && !isActive) {
    return <RedirectToLogin />;
  }

  if (isLoading) {
    return <LoadingView />;
  }

  const subscriptionIsActive = subscription.status === ACTIVE_STATUS;

  return (
    <StyledAccountContainer>
      <AccountHeader
        links={links}
        logoUrl={logoUrl}
        logoSrText={logoSrText}
        name={customer.first_name}
      />
      <ContentContainer>
        <HeaderContainer>
          <WelcomeBack
            headline={interpolate(headline, { name: customer.first_name })}
            subheadline={interpolate(subheadline, { petName: pet.name })}
          />
          <DogSelection
            options={petOptions}
            onChange={onSelectPet}
            value={selectedPetId}
            label={dogSelectorLabel}
            addPupLinkUrl={addPupBtnUrl}
            addPupLinkLabel={addPupBtnLabel}
          />
        </HeaderContainer>
        <YourPupContainer>
          <YourPup
            {...getYourPupProps(yourPup, pet, customer, latestOrder)}
            hasActiveSubscription={subscriptionIsActive}
          />
        </YourPupContainer>
        {subscriptionIsActive && (
          <>
            <SectionContainer>
              <NextBox
                {...getNextBoxProps({
                  subscription,
                  product,
                  pet,
                  nextDeliveryDate,
                  order: pendingOrder,
                })}
              />
            </SectionContainer>
            <SectionContainer>
              <BoxManagementNav
                {...getBoxManagementNavProps({
                  nextDeliveryDate,
                  subscription,
                  address,
                  payment,
                  pet,
                  product,
                })}
                petId={selectedPetId}
                brightbackInfo={{
                  customerId: customer.id,
                  customerEmail: customer.email,
                  petSubscriptionId: pet.subscription_id,
                }}
              />
            </SectionContainer>
            <SectionContainer>
              <VetServices
                {...VET_SERVICES}
                onCtaClick={() => {
                  reporter.tag(openVetServicesModalEvent());
                  setVetServicesModalShown(true);
                }}
              />
            </SectionContainer>
            <VetServicesModal
              {...VET_SERVICES_MODAL}
              shown={vetServicesModalShown}
              onClickClose={() => setVetServicesModalShown(false)}
              onCtaClick={onVetCtaClick}
            />
          </>
        )}
      </ContentContainer>
      <ContentContainer>
        <BlogLinks
          blogs={blogs}
          heading={blogHeading}
          fontColor={blogFontColor}
        />
      </ContentContainer>
      <ContentContainer flex={true}>
        <LearnMore
          img={learnMoreImg}
          heading={learnMoreHeading}
          text={learnMoreBody}
          learnMoreBtnUrl={learnMoreHref}
          learnMoreBtnLabel={learnMoreBtnLabel}
        />
        <ReferAFriend discountAmount={discountAmount} />
      </ContentContainer>
      <Footer {...FOOTER_CONTENT} />
    </StyledAccountContainer>
  );
};
AccountContainer.displayName = 'AccountContainer';
AccountContainer.propTypes = {
  headline: PropTypes.string.isRequired,
  subheadline: PropTypes.string.isRequired,
  dogSelectorLabel: PropTypes.string.isRequired,
  addPupBtnLabel: PropTypes.string.isRequired,
  addPupBtnUrl: PropTypes.string.isRequired,
  links: PropTypes.array.isRequired,
  logoUrl: PropTypes.string.isRequired,
  logoSrText: PropTypes.string.isRequired,
  yourPup: PropTypes.object.isRequired,
  discountAmount: PropTypes.number.isRequired,
  learnMoreBtnLabel: PropTypes.string.isRequired,
  learnMoreHeading: PropTypes.string.isRequired,
  learnMoreBody: PropTypes.string.isRequired,
  learnMoreHref: PropTypes.string.isRequired,
  learnMoreImg: PropTypes.shape({
    src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    alt: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
  blogs: PropTypes.arrayOf(
    PropTypes.shape({
      blogLink: PropTypes.string.isRequired,
      topic: PropTypes.string.isRequired,
      title: PropTypes.object.isRequired,
      image: PropTypes.object.isRequired,
    }).isRequired,
  ).isRequired,
  blogHeading: PropTypes.string.isRequired,
  blogFontColor: PropTypes.string.isRequired,
};
